import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Content, EnumSelectControl, Form, FormSubmit, NumberInputControl, TextInputControl } from '@/core/components';

import { UserSelectControl } from '../Nomen/User/UserSelectControl';
import { FacultySchema } from './model/FacultySchema';
import { facultyDomainList } from './model/FacultyTypes';

interface FacultyFormProps extends FormSubmit<FacultySchema> {
  title: string;
}

export function FacultyForm(props: FacultyFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<FacultySchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  const facultyDomainOptions = facultyDomainList.map(c => ({ value: c, label: t(`faculties.domainEnum.${c}`) }));

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<FacultySchema>
              name="name"
              label={t('faculties.form.fields.name')}
              isSubmitting={isSubmitting}
            />
            <UserSelectControl<FacultySchema>
              label={t('faculties.form.fields.headOfFaculty')}
              name="headOfFaculty"
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<FacultySchema>
              name="teachersNo"
              label={t('faculties.form.fields.teachersNo')}
              isSubmitting={isSubmitting}
            />
            <EnumSelectControl<FacultySchema>
              options={facultyDomainOptions}
              name="domain"
              label={t('faculties.form.fields.domain')}
              isSubmitting={isSubmitting}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
